// Build-version surface: which UI bundle is loaded, and which API build is
// answering it.
//
// The UI version is baked in at build time by Vite (VITE_BUILD_VERSION, set by
// packaging/build-debs.ps1 and .github/workflows/build.yml from the deb
// version). The API version comes from GET /api/version, which is anonymous so
// this works on the login screen too.
//
// Showing BOTH matters: the UI and the API ship as separate .debs, so a deploy
// can land one and not the other. A version mismatch here is the cheapest way
// to catch "I deployed but nothing changed" before digging into logs.

import { useEffect, useState } from 'react';
import { Tag, AlertTriangle } from 'lucide-react';
import { API_BASE } from '@/env';

export const UI_BUILD_VERSION = import.meta.env.VITE_BUILD_VERSION ?? 'dev';

/** Re-checked periodically so a deploy landing under an open tab shows up. */
const POLL_MS = 5 * 60 * 1000;

interface ApiVersion {
  version: string;
  startedAtUtc: string;
  assemblyMtimeUtc: string | null;
}

// InformationalVersion often carries a "+<sha>" suffix that the deb version
// doesn't, so compare on the release part only. Deb versions built by
// build-debs.ps1 also carry a "~dev<stamp>.<sha>" pre-release suffix
// (1.8.0~dev20260825161122.gee12232), which is noise for a comparison.
const core = (v: string) => v.split('+')[0].split('~')[0].trim();

/** The key builds are COMPARED on. Drops only the "+<sha>" that InformationalVersion carries and
 *  the deb version doesn't — the "~dev<stamp>.<sha>" pre-release suffix is KEPT, because that is
 *  the only thing distinguishing two unversioned build-debs.ps1 runs. Comparing on `core` instead
 *  collapsed both to "1.8.0" and reported a match during exactly the half-applied deploy this
 *  badge exists to catch. */
const compareKey = (v: string) => v.split('+')[0].trim();

/**
 * What the status bar prints. Two full deb version strings side by side don't
 * fit a 28px footer between the sm and lg breakpoints, so the inline form is
 * the release number only — the complete strings stay in the tooltip.
 */
const short = (v: string | null | undefined) => (v ? core(v) : '…');

/** A version we can meaningfully compare — local dev builds and an API that
 *  can't read its own assembly attribute are both excluded. */
const comparable = (v: string | undefined | null) =>
  !!v && v !== 'dev' && v !== 'unknown';

export interface BuildVersionInfo {
  ui: string;
  api: string | null;
  apiStartedAtUtc: string | null;
  /** Both versions are known and disagree — a half-applied deploy. */
  mismatch: boolean;
  unreachable: boolean;
}

export function useBuildVersion(): BuildVersionInfo {
  const [api, setApi] = useState<ApiVersion | null>(null);
  const [unreachable, setUnreachable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        // Plain fetch, not apiFetch: /api/version is [AllowAnonymous], and
        // routing it through the auth client would trigger token refresh
        // attempts on the login screen.
        const res = await fetch(`${API_BASE}/api/version`);
        if (!res.ok) {
          // The API ANSWERED — it is reachable, we just didn't get a version this cycle.
          // /api/version shares the 600-req/min client partition with every other controller,
          // so a 429 here is routine; reporting "API down" for the next five minutes off the
          // back of one would be actively misleading. Keep the last known version and retry.
          if (!cancelled) setUnreachable(false);
          console.debug('[BuildVersion] /api/version returned', res.status, '- keeping last known version');
          return;
        }
        const body: ApiVersion = await res.json();
        if (cancelled) return;
        setApi(body);
        setUnreachable(false);
      } catch {
        // Only a TRANSPORT failure lands here now. Drop the cached version too: keeping it
        // leaves `mismatch` true and the tooltip quoting a version we can no longer see, which
        // reads as a half-applied deploy rather than an outage.
        if (!cancelled) { setUnreachable(true); setApi(null); }
      }
    };
    load();
    const t = setInterval(load, POLL_MS);
    return () => { cancelled = true; clearInterval(t); };
  }, []);

  const apiVersion = api?.version ?? null;
  return {
    ui: UI_BUILD_VERSION,
    api: apiVersion,
    apiStartedAtUtc: api?.startedAtUtc ?? null,
    // An unreachable API is an outage, not a version mismatch — reporting both
    // at once would be contradictory.
    mismatch:
      !unreachable &&
      comparable(UI_BUILD_VERSION) && comparable(apiVersion) &&
      compareKey(UI_BUILD_VERSION) !== compareKey(apiVersion!),
    unreachable,
  };
}

function tooltip(v: BuildVersionInfo): string {
  const lines = [`UI bundle: ${v.ui}`, `API: ${v.unreachable ? 'unreachable' : (v.api ?? '…')}`];
  if (v.apiStartedAtUtc) {
    const started = new Date(v.apiStartedAtUtc);
    if (!isNaN(started.getTime())) lines.push(`API started: ${started.toLocaleString()}`);
  }
  if (v.mismatch) lines.push('UI and API builds differ — a deploy may be half-applied.');
  return lines.join('\n');
}

/**
 * Inline version readout for a chrome bar that has room for it (the dashboard
 * status bar). Shows both builds, and calls out a mismatch.
 */
export function BuildVersionStatus({ className }: { className?: string }) {
  const v = useBuildVersion();

  return (
    <span
      className={`inline-flex items-center gap-1 font-mono tabular-nums ${
        v.mismatch ? 'text-amber-500 font-semibold' : ''
      } ${className ?? ''}`}
      title={tooltip(v)}
    >
      {v.mismatch
        ? <AlertTriangle className="h-3 w-3 shrink-0" />
        : <Tag className="h-3 w-3 shrink-0 opacity-60" />}
      <span>UI {short(v.ui)}</span>
      <span className={v.mismatch ? 'opacity-80' : 'text-muted-foreground/40'}>
        {v.mismatch ? '≠' : '·'}
      </span>
      <span className={v.unreachable ? 'text-red-500' : undefined}>
        API {v.unreachable ? 'down' : short(v.api)}
      </span>
    </span>
  );
}

/**
 * Floating corner badge for screens without their own chrome bar (login,
 * shared scenario view, …). Suppressed on the dashboard, which renders
 * BuildVersionStatus in its status bar instead — otherwise the two overlap in
 * the same bottom-right corner.
 */
/** The router strips an optional leading slash, so #dashboard and #/dashboard
 *  are the same route — match both, or the badge stays visible on top of the
 *  status readout it is supposed to defer to. */
const isDashboardHash = () => /^#\/?dashboard\b/.test(window.location.hash);

export function BuildVersionBadge() {
  const v = useBuildVersion();
  const [onDashboard, setOnDashboard] = useState(isDashboardHash);

  useEffect(() => {
    const onHash = () => setOnDashboard(isDashboardHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  // pointer-events-none + select-none keep the badge from intercepting clicks
  // or selection on the underlying app. text-muted-foreground keeps it
  // readable across light + dark themes. (The title is inert under
  // pointer-events-none; it's there for the sm:hidden-off case on mobile,
  // where the badge is the only version surface.)
  return (
    <div
      // On the dashboard the status bar carries the version instead — but that
      // footer is `hidden sm:flex`, so below `sm` it isn't rendered at all and
      // suppressing the badge outright left phones with no version anywhere.
      // Hide it only where the footer actually takes over.
      className={`fixed bottom-1 right-2 z-50 px-1.5 py-0.5 text-[10px] font-mono select-none pointer-events-none ${
        onDashboard ? 'sm:hidden' : ''
      } ${v.unreachable ? 'text-red-500 opacity-90' : v.mismatch ? 'text-amber-500 opacity-90' : 'text-muted-foreground opacity-40'}`}
      title={tooltip(v)}
    >
      {/* Rendered inline, not just in the title: this element is pointer-events-none, so the
          tooltip can never be opened and an outage would otherwise be completely invisible on
          the login, public-share and mobile-dashboard screens. */}
      v{short(v.ui)}{v.unreachable ? ' · api down' : v.mismatch && v.api ? ` ≠ api ${short(v.api)}` : ''}
    </div>
  );
}
